import React, { useEffect, useState } from "react";
import { useUser } from "../contexts/UserContext";
import { useNavigate } from "react-router-dom";

const SignIn = () => {
  const { user, signInWithGoogle, signInWithEmail, signUpWithEmail } = useUser();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isCreatingAccount, setIsCreatingAccount] = useState(false);

  useEffect(() => {
    if (user) {
      navigate("/recipes");
    }
  }, [user, navigate]);

  const handleGoogleSignIn = async () => {
    try {
      await signInWithGoogle();
      navigate("/recipes");
    } catch (error) {
      console.error("Signin failed:", error);
    }
  };

  const handleEmailSubmit = async (event) => {
    event.preventDefault();
    setError(null);
    if (isCreatingAccount && password !== confirmPassword) {
      setError("Passwords do not match. Please confirm your password.");
      return;
    }
    setIsSubmitting(true);

    try {
      if (isCreatingAccount) {
        await signUpWithEmail(email, password);
      } else {
        await signInWithEmail(email, password);
      }
      navigate("/recipes");
    } catch (error) {
      let message = isCreatingAccount
        ? "Unable to create an account with the provided email and password."
        : "Unable to sign in with email and password.";

      if (error.code === "auth/invalid-email") {
        message = "Please enter a valid email address.";
      } else if (!isCreatingAccount && (error.code === "auth/user-not-found" || error.code === "auth/wrong-password")) {
        message = "The email or password you entered is incorrect.";
      } else if (isCreatingAccount && error.code === "auth/email-already-in-use") {
        message = "An account already exists for this email address. Try signing in instead.";
      } else if (isCreatingAccount && error.code === "auth/weak-password") {
        message = "Passwords must be at least 6 characters long.";
      }
      setError(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="flex flex-col items-center px-4">
      <h1 className="text-primary text-3xl font-bold">
        {isCreatingAccount ? "Create account" : "Sign in"}
      </h1>
      <form onSubmit={handleEmailSubmit} className="w-full max-w-md bg-white rounded-lg shadow-md p-6 space-y-4">
        {error && <p className="text-red-600 text-sm" role="alert">{error}</p>}
        <div className="flex flex-col">
          <label htmlFor="email" className="text-sm font-medium text-gray-700">Email</label>
          <input
            id="email"
            type="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            className="mt-1 rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            autoComplete="email"
            required
          />
        </div>
        <div className="flex flex-col">
          <label htmlFor="password" className="text-sm font-medium text-gray-700">Password</label>
          <input
            id="password"
            type="password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            className="mt-1 rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            autoComplete={isCreatingAccount ? "new-password" : "current-password"}
            required
          />
        </div>
        {isCreatingAccount && (
          <div className="flex flex-col">
            <label htmlFor="confirmPassword" className="text-sm font-medium text-gray-700">
              Confirm password
            </label>
            <input
              id="confirmPassword"
              type="password"
              value={confirmPassword}
              onChange={(event) => setConfirmPassword(event.target.value)}
              className="mt-1 rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              autoComplete="new-password"
              required
            />
          </div>
        )}
        <button
          type="submit"
          className="w-full rounded-md bg-primary py-2 text-white font-semibold hover:bg-primary-hover focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-70"
          disabled={isSubmitting}
        >
          {isSubmitting
            ? isCreatingAccount
              ? "Creating account..."
              : "Signing in..."
            : isCreatingAccount
              ? "Create account"
              : "Sign in"}
        </button>
      </form>
      <button
        type="button"
        className="mt-4 text-sm text-primary hover:text-primary-hover"
        onClick={() => {
          setIsCreatingAccount((previous) => !previous);
          setError(null);
          setConfirmPassword("");
        }}
      >
        {isCreatingAccount ? "Already have an account? Sign in" : "Need an account? Create one"}
      </button>
      <div className="mt-8 flex flex-col items-center space-y-3">
        <span className="text-gray-500 text-sm">or continue with</span>
        <button onClick={handleGoogleSignIn} className="flex items-center justify-center w-full max-w-xs py-3 px-4 text-sm font-semibold text-gray-600 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-gray-300">
          <img
            className="w-5 h-5 mr-2"
            src="https://www.svgrepo.com/show/475656/google-color.svg"
            alt="Google logo"
          />
          Sign in with Google
        </button>
      </div>
    </div>
  );
};

export default SignIn;